/**
 * Pre-check that mirrors the worker's commit-time guard `encodedCoversDuration`
 * (workers/src/coordinator.ts). The annotation UI encodes exactly `totalSeconds`
 * cells (one bit per 1/fps-second), so the payload sent at commit covers
 * `ceil(totalSeconds / 8) * 8` bits. The worker rejects a commit with
 * "Encoded payload too short for episode duration" when those bits cannot cover
 * the DDB duration (`episodeDuration * fps`).
 *
 * `totalSeconds` is `ceil(videoDuration * fps)` — derived from the preview
 * video's real duration — so evaluating this at load time predicts the commit
 * outcome EXACTLY: it returns true only for previews that would actually fail to
 * commit, and never for previews that would commit fine.
 *
 * It is intentionally NOT a float comparison of the two durations. The ceil() +
 * byte-alignment slack absorbs sub-second jitter (at fps=1 a preview can be a
 * fraction — or even up to ~1s, depending on duration mod 8 — shorter than the
 * DDB value and still commit), so a healthy episode is never flagged. Only a
 * preview short enough to genuinely trip the commit guard returns true.
 */
export function isPreviewDurationMismatch(
  totalSeconds: number,
  episodeDuration: number | null | undefined,
  fps: number
): boolean {
  if (episodeDuration == null || episodeDuration <= 0) return false;
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return false;
  const effectiveFps = Math.max(1, Math.floor(fps || 1));
  const availableBits = Math.ceil(totalSeconds / 8) * 8;
  const requiredBits = episodeDuration * effectiveFps;
  return availableBits < requiredBits;
}
